import { useEffect, useMemo } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiBox,
  MiBuildPageHeader,
  MiButton,
  MiIcon,
  MiTab,
} from "@miplatform/components";
import {
  BUTTON_VARIANTS,
  ENTITY_TYPES,
  PROPERTY_TYPE,
} from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import { Colors } from "@miplatform/types";
import { mdiCalendarBlankOutline, mdiPlus } from "@mdi/js";
import {
  OrderActivityDrawer,
  SignalRConnectionIndicator,
} from "@mibuildweb/components";
import { Box } from "@mui/material";
import { CalendarProvider, useCalendarContext } from "../../providers";
import { useStoredCalendarSet } from "../../stores";
import { OrdersProvider } from "../Orders/OrdersProvider";
import { CreateNewOrderDrawer } from "../Schedule/Scheduler/CreateNewOrderDrawer/CreateNewOrderDrawer";
import { CalendarRenderer } from "./CalendarRenderer";
import { NhcBoardHeaderCenter } from "./NhcBoardHeaderCenter";
import { useNhcBoardViewModel } from "./useNhcBoardViewModel";

const TypedOrderActivityDrawer =
  OrderActivityDrawer as React.ComponentType<any>;

export const NhcBoard = () => {
  return (
    <OrdersProvider>
      <CalendarProvider>
        <NhcBoardContent />
      </CalendarProvider>
    </OrdersProvider>
  );
};

const NhcBoardContent = () => {
  const { classes } = useStyles();
  const { params } = useRouter();
  const { calendarSetId, calendarId } = params;

  const { calendarSet } = useStoredCalendarSet(calendarSetId ?? null);
  const { config: calendarSetConfig } = calendarSet ?? {};

  const {
    orderReasons,
    getOrderReasons,
    setSelectedOrder,
    selectedOrder,
    orderDetails,
    getOrderById,
    clearOrderDetails,
    refreshOrder,
    getOrderActivity,
    handleTabChange,
    isCreateNewOrderDrawerOpen,
    setIsCreateNewOrderDrawerOpen,
    handleAfterOrderCreation,
  } = useNhcBoardViewModel();

  const { calendarConfig, calendarCounts } = useCalendarContext();

  const tabs = useMemo(
    () =>
      calendarSetConfig?.calendarConfigs?.map((calendar) => ({
        id: calendar.id,
        value: calendar.id,
        label: calendar.name,
        count: calendarCounts[calendar.id] || 0,
        ...(calendarCounts[calendar.id]
          ? { chipLabel: calendarCounts[calendar.id], chipColor: Colors.Red }
          : {}),
      })),
    [calendarSetConfig, calendarCounts]
  );

  useEffect(() => {
    if (selectedOrder?.assignment?.order?.orderId) {
      getOrderById(selectedOrder.assignment.order.orderId);
    }
  }, [selectedOrder]);

  useEffect(() => {
    getOrderReasons();
  }, [calendarSetConfig]);

  return (
    <>
      <NhcBoardHeader
        key={calendarId}
        setIsCreateNewOrderDrawerOpen={setIsCreateNewOrderDrawerOpen}
      />
      <MiTab
        tabs={tabs}
        currentTab={calendarConfig?.id}
        onTabChange={handleTabChange}
      />
      <Box display="flex" flexDirection="row" flex={1} overflow="hidden">
        <MiBox className={classes.orderBoardContainer}>
          {calendarConfig && (
            <CalendarRenderer
              setSelectedOrder={setSelectedOrder}
              orderReasons={orderReasons}
            />
          )}
        </MiBox>
        <TypedOrderActivityDrawer
          open={!!orderDetails?.id}
          onClose={clearOrderDetails}
          order={orderDetails}
          getOrderActivity={getOrderActivity}
          onRefresh={refreshOrder}
        />

        {isCreateNewOrderDrawerOpen && (
          <CreateNewOrderDrawer
            open={isCreateNewOrderDrawerOpen}
            onClose={handleAfterOrderCreation}
            defaultPropertyType={PROPERTY_TYPE.NEW_HOME_CONSTRUCTION}
          />
        )}
      </Box>
    </>
  );
};

const NhcBoardHeader = ({
  setIsCreateNewOrderDrawerOpen,
}: {
  setIsCreateNewOrderDrawerOpen: (open: boolean) => void;
}) => {
  const { theme } = useStyles();

  const { calendarSet, search, connectionStatus, handleCalendarSearch } =
    useCalendarContext();
  return (
    <MiBuildPageHeader
      title="New Home Construction"
      search={search}
      onHandleSearch={(e: React.ChangeEvent<HTMLInputElement>) =>
        handleCalendarSearch(e.target.value)
      }
      centerComponent={<NhcBoardHeaderCenter />}
      leftComponent={
        <SignalRConnectionIndicator connectionStatus={connectionStatus} />
      }
      titleIcon={
        <MiIcon
          path={mdiCalendarBlankOutline}
          size={1}
          color={theme.palette.primary.main}
        />
      }
      actionComponent={
        calendarSet?.config.eventType !== ENTITY_TYPES.ORDER ? null : (
          <MiButton
            title="Create New"
            variant={BUTTON_VARIANTS.CONTAINED}
            startIcon={{
              path: mdiPlus,
            }}
            onClick={() => setIsCreateNewOrderDrawerOpen(true)}
          />
        )
      }
    />
  );
};

const useStyles = makeStyles()(() => ({
  orderBoardContainer: {
    height: "inherit",
    display: "flex",
    flexDirection: "column",
    flex: 1,
    padding: 0,
  },
  actionContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
  },
}));
